import { authedFetch } from '../lib/authedFetch';
import { cseConflictKey } from '../lib/cseConflicts';
import { canonicalJson } from '../lib/reportGeneration/reportContractHash';
import {
  cseReportReviewAckSchema, cseReportReviewContextSchema, saveCseReportReviewRequestSchema,
  type CseReportReviewContext, type CseReportReviewAck, type SaveCseReportReviewRequest,
} from '../types/cseReportReview';

function route(reportId: string, ownerUid: string): string {
  return `/api/report-cse-reviews/${encodeURIComponent(reportId)}?ownerUid=${encodeURIComponent(ownerUid)}`;
}
async function responseBody(response: Response): Promise<unknown> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? body.error : 'Report review could not be confirmed. Reload before retrying.';
    throw new Error(error);
  }
  return body;
}
export async function loadCseReportReviewContext(reportId: string, ownerUid: string, signal?: AbortSignal): Promise<CseReportReviewContext> {
  const body = await responseBody(await authedFetch(route(reportId, ownerUid), { signal }));
  const context = cseReportReviewContextSchema.parse(body);
  if (context.basis.reportId !== reportId || context.basis.ownerUid !== ownerUid) throw new Error('Report review source identity does not match this report.');
  if (context.review) {
    const allowed = new Map(context.conflicts.map(row => [cseConflictKey(row.reportSectionId, row.field), row.values]));
    if (canonicalJson(context.review.basis) !== canonicalJson(context.basis)
      || allowed.size !== context.review.choices.length
      || context.review.choices.some(row => !allowed.get(row.conflictKey)?.includes(row.value))) {
      throw new Error('The retained review does not match this saved report source.');
    }
  }
  return context;
}
export async function saveCseReportReview(request: SaveCseReportReviewRequest, signal?: AbortSignal): Promise<CseReportReviewAck> {
  const selected = saveCseReportReviewRequestSchema.parse(request);
  const body = await responseBody(await authedFetch(route(selected.basis.reportId, selected.basis.ownerUid), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(selected), signal,
  }));
  const ack = cseReportReviewAckSchema.parse(body);
  const sort = (rows: typeof selected.choices) => [...rows].sort((a, b) => a.conflictKey.localeCompare(b.conflictKey));
  if (ack.review.operationId !== selected.operationId || canonicalJson(ack.review.basis) !== canonicalJson(selected.basis)
    || canonicalJson(sort(ack.review.choices)) !== canonicalJson(sort(selected.choices))) {
    throw new Error('The saved report review does not match the submitted choices. Reload before retrying.');
  }
  return ack;
}
